import React from 'react';
import Dropdown from '../../components/ui/Dropdown';
import FormField from '../../components/ui/FormField';
import FormRow from '../../components/ui/FormRow';

// Shared transport field-group: the "Personal transport" single-select plus the
// coupled "Shared transport options" multi-select. Extracted from the Journey
// Planner so the same control (and its behaviour) is reused by the My Trips
// New/Edit trip form. This is a field-level component, not form orchestration —
// each consumer keeps its own form shell, validation, and submit.

export const PERSONAL_TRANSPORT_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'walking', label: 'Walking' },
  { value: 'bicycle', label: 'Bicycle' },
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'car', label: 'Car' },
  { value: 'camper_van', label: 'Camper van' },
];

export const SHARED_TRANSPORT_OPTIONS = [
  { value: 'train', label: 'Train 🚆', emoji: '🚆' },
  { value: 'bus', label: 'Bus 🚌', emoji: '🚌' },
  { value: 'airplane', label: 'Airplane ✈️', emoji: '✈️' },
  { value: 'car_sharing', label: 'Car Sharing 🚗', emoji: '🚗' },
  { value: 'boat', label: 'Boat ⛴️', emoji: '⛴️' },
];

// Shared modes only apply when the traveller isn't using their own motorised
// transport (motorcycle / car / camper van pick their own way). Walking and
// bicycle are non-motorised: they coexist with shared modes, same as none.
// Keep in sync with the backend mirrors (tripService.assertTransportSelected,
// journeyGenerationService's transport-constraints branch).
export const usesSharedTransport = (transportPreference: string): boolean =>
  transportPreference === 'none' || transportPreference === 'bicycle' || transportPreference === 'walking';

// Flying is not a separate field: it's allowed iff 'airplane' is among the
// selected shared modes (and only shared transport can include flying).
export const deriveFlyingAllowed = (transportPreference: string, sharedModes: string[]): boolean =>
  usesSharedTransport(transportPreference) && sharedModes.includes('airplane');

export interface TransportFieldsProps {
  transportPreference: string;
  sharedModes: string[];
  onTransportPreferenceChange: (value: string) => void;
  onSharedModesChange: (modes: string[]) => void;
  personalLabel?: string;
  sharedLabel?: string;
}

const TransportFields: React.FC<TransportFieldsProps> = ({
  transportPreference,
  sharedModes,
  onTransportPreferenceChange,
  onSharedModesChange,
  personalLabel = 'Personal transport',
  sharedLabel = 'Shared transport options',
}) => (
  <FormRow>
    {/* Both controls use the same styled Dropdown — personal is single-select,
        shared is multi-select — so the two look and behave consistently. */}
    <FormField label={personalLabel}>
      <Dropdown
        ariaLabel={personalLabel}
        options={PERSONAL_TRANSPORT_OPTIONS}
        value={transportPreference}
        onChange={onTransportPreferenceChange}
      />
    </FormField>
    <div
      className={`transition-all duration-300 ease-in-out ${
        usesSharedTransport(transportPreference)
          ? 'max-h-96 opacity-100'
          : 'max-h-0 opacity-0 overflow-hidden'
      }`}
    >
      <FormField label={sharedLabel}>
        <Dropdown
          multiple
          ariaLabel={sharedLabel}
          placeholder="Select shared transport options"
          options={SHARED_TRANSPORT_OPTIONS}
          value={sharedModes}
          onChange={onSharedModesChange}
        />
      </FormField>
    </div>
  </FormRow>
);

export default TransportFields;
